import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Chip } from '@mui/material';
import { deleteDestination, getDestinations } from '../../../../../api/endpoints/cmsContent';
import type { Destination } from '../../../../../api/types/cmsContent';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../../components/common';
import { DestinationFormDialog } from '../DestinationFormDialog';

/** `SiteDetailPage` içinde bir sekme; `siteId` sabit prop. `GetDestinationById` yok —
 *  edit diyaloğu satırı doğrudan bu sayfanın cache'inden alır. */
export default function DestinationListPage({ siteId }: { siteId: number }) {
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Destination | null>(null);

  const query = useQuery({
    queryKey: ['cms', 'content', 'destinations', siteId],
    queryFn: () => getDestinations(siteId),
  });
  const rows: Destination[] = useMemo(() => query.data?.destinations ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.title, row.summary, row.link]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const del = useDeleteResource<number>({
    mutationFn: (id) => deleteDestination(id),
    invalidateKeys: [['cms', 'content', 'destinations']],
    successMessage: 'Destinasyon silindi.',
  });
  const deletingName = rows.find((r) => r.id === del.target)?.title ?? '';

  const columns: DataTableColumn<Destination>[] = [
    { id: 'title', header: 'Başlık', cell: (r) => r.title ?? '—' },
    { id: 'lang', header: 'Dil', cell: (r) => (r.language ? <Chip size="small" label={r.language} /> : '—') },
    { id: 'link', header: 'Link', cell: (r) => r.link ?? '—' },
    { id: 'detail', header: 'Detay Sayısı', cell: (r) => r.destinationDetails?.length ?? 0 },
  ];

  return (
    <ListPageScaffold
      title="Destinasyonlar"
      headerVariant="section"
      createLabel="Yeni Destinasyon"
      onCreate={() => setCreateOpen(true)}
      search={{ value: search, onChange: setSearch, placeholder: 'Başlık, özet, link...' }}
      query={query}
      rows={filteredRows}
      columns={columns}
      getRowKey={(r) => r.id}
      emptyTitle={
        rows.length === 0 ? 'Kayıtlı destinasyon bulunamadı.' : 'Aramayla eşleşen destinasyon bulunamadı.'
      }
      rowActions={(r) => <RowActions onEdit={() => setEditing(r)} onDelete={() => del.request(r.id)} />}
    >
      <DestinationFormDialog open={createOpen} destination={null} siteId={siteId} onClose={() => setCreateOpen(false)} />
      <DestinationFormDialog open={!!editing} destination={editing} siteId={siteId} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Destinasyonu sil"
        description={`"${deletingName}" destinasyonunu silmek istediğinize emin misiniz?`}
      />
    </ListPageScaffold>
  );
}
